import { generateDropinAppearance, generateSemanticColors, resolveMode } from '@billing/branding';
import type { BrandingSettings, ResolvedMode, ThemeColorConfig, ThemeMode } from '@billing/branding';
import { SEMANTIC_COLORS_DARK } from '@airwallex/theme-generator';
import type { SeedColors } from '@airwallex/theme-generator';

import type { PaymentPageThemeDto } from '@/api/theme';
import type { ServerInvoiceRes } from '@/types/checkout';
import type { DropInAppearance } from '@/components/loadPaymentElement';

/** Canonical colour mode (PAA-3007). `SYSTEM` follows the reader's device. */
export type ThemeColorMode = ThemeMode;

export type ThemeSeedColors = {
  colorBrand?: string;
  colorText?: string;
  colorBackground?: string;
};

export interface ThemeConfig {
  /** Resolved colour mode. `SYSTEM` follows the reader's device (PAA-3012). */
  colorMode?: ThemeColorMode;
  /**
   * Which seed-configuration path the merchant used. `SIMPLE` exposes an accent
   * only — background/ink are synthesised from it; `ADVANCED` sets all three.
   */
  colorConfig?: ThemeColorConfig;
  seedColors: SeedColors;
  brandingSettings: BrandingSettings;
  // True for the base Airwallex (default) theme, false for a saved custom theme
  // (PAA-2711). Consumed by the Xero ACC accent logic via ThemedBrandingShell.
  isDefault?: boolean;
}

const THEME_MODES: readonly ThemeColorMode[] = ['LIGHT', 'DARK', 'SYSTEM'];
const THEME_COLOR_CONFIGS: readonly ThemeColorConfig[] = ['SIMPLE', 'ADVANCED'];

const pickString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

/*
 * Uppercase a wire enum value and keep it only when it names a known member.
 * The wire is untrusted, so unknown values normalize to `undefined` rather than
 * flowing into the palette generator.
 *
 * @param value The raw wire value.
 * @param allowed The members this enum accepts.
 * @return The matching member, or `undefined` when the value is not one.
 */
const normalizeEnum = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined => {
  const upper = pickString(value)?.toUpperCase();
  return allowed.find((member) => member === upper);
};

/*
 * The seeds a theme actually carries, after the `colorConfig` rule.
 *
 * A `SIMPLE` theme exposes only an accent: its background and ink are
 * synthesised from that accent, so any `colorBackground` / `colorText` still
 * sitting on the theme are leftovers from a previous `ADVANCED` save and must
 * not be honoured. Dropping them here — at the shared boundary every consumer
 * reads seeds through — keeps the synthesised palette in step with the raw seed
 * consumers (page background, drop-in appearance), which would otherwise paint
 * the stale colours. `ADVANCED` (and a legacy theme with no `colorConfig`) is
 * returned untouched. (PAA-3011)
 *
 * @param theme The canonical theme, if one is in scope.
 * @return The seeds the theme may actually use, or `undefined` when it has none.
 */
export const effectiveSeedColors = (theme?: ThemeConfig): ThemeSeedColors | undefined => {
  const seeds = theme?.seedColors;
  if (!seeds) return seeds;
  if (theme?.colorConfig !== 'SIMPLE') return seeds;
  return { colorBrand: seeds.colorBrand };
};

/**
 * The default LHS (order-info) page background — the Airwallex neutral
 * `grays[10]`. A stable, explicit constant rather than a lookup into
 * `semanticColors.background.secondary`, so the un-themed page is pinned even if
 * the generator's derived shades shift (PAA-2711).
 */
export const DEFAULT_PAGE_BACKGROUND = '#f5f6f7';

/*
 * The LHS (order-info) *page* background.
 *
 * The merchant's raw `colorBackground` seed when the theme actually carries one
 * (ADVANCED/legacy), otherwise the resolved mode's neutral page background.
 * `SIMPLE` themes expose an accent only, so a leftover background must not paint
 * the page. (PAA-2711, PAA-3012)
 *
 * Resolution by theme shape (PAA-3188):
 * - ADVANCED/legacy with a `colorBackground` seed → that raw seed (exact colour).
 * - SIMPLE → the SDK's colorConfig-aware synthesised background (a faintly
 *   brand-tinted paper derived from the accent, mode-aware), so the order-info
 *   half matches the SIMPLE cards and the merchant email rather than a flat
 *   neutral — and never the stale ADVANCED background seed, which
 *   `effectiveSeedColors` has already stripped.
 * - Un-themed / legacy with no seed → the resolved mode's neutral page
 *   background (`DEFAULT_PAGE_BACKGROUND` == LIGHT `background.secondary` for
 *   light, DARK `background.secondary` for dark, so a dark theme is not a bright
 *   white band next to the dark payment column).
 *
 * `resolvedMode`, when supplied, is the already-resolved paint mode from
 * `useResolvedThemeMode`, so a live `SYSTEM` flip is honored; without it the
 * mode is resolved here from the theme.
 *
 * @param theme The canonical theme, if one is in scope.
 * @param resolvedMode The mode already resolved for paint, when known.
 * @return The colour to paint the order-info half with.
 */
export const pickLhsPageBackground = (theme?: ThemeConfig, resolvedMode?: ResolvedThemeMode): string => {
  const seeds = effectiveSeedColors(theme);
  if (seeds?.colorBackground) return seeds.colorBackground;
  const mode = resolvedMode ?? resolveMode(theme?.colorMode ?? 'LIGHT');
  if (theme?.colorConfig === 'SIMPLE') {
    return generateSemanticColors({ colorConfig: 'SIMPLE', mode, seedColors: { colorBrand: seeds?.colorBrand } })
      .background.primary;
  }
  return mode === 'DARK' ? SEMANTIC_COLORS_DARK.background.secondary : DEFAULT_PAGE_BACKGROUND;
};

// True when at least one value in the object is a non-empty string. Used by the
// shell to decide whether a usable theme is present (e.g. whether to paint the
// merchant background).
export const hasAnyValue = (obj?: Record<string, string | undefined>): boolean =>
  Boolean(obj && Object.values(obj).some((v) => typeof v === 'string' && v.length > 0));

/**
 * Any wire theme this repo receives.
 *
 * The endpoints disagree on exactly one thing: whether the seeds and branding
 * sit at the top level (the flat `PaymentPageThemeDto` returned by the
 * payment-link endpoints and by Xero's `getTheme`) or under `seedColors` /
 * `brandingSettings` (the nested shape on the invoice, success and preview
 * payloads). Everything else — the enum values, the `SIMPLE` stripping rule,
 * the canonical output — is identical.
 *
 * The two field sets are disjoint, so the normalizer reads both spellings and a
 * call site does not have to know which one its endpoint returns. Before this
 * was one boundary, each domain picked an adapter by inspection and a wrong pick
 * silently produced an empty theme rather than a type error (PAA-3012).
 */
export type WireThemeInput = ServerInvoiceRes['theme'] | PaymentPageThemeDto;

/*
 * The single theme boundary: untrusted wire payload in, canonical `ThemeConfig`
 * out.
 *
 * Every producer in the repo funnels through here — the Xero invoice theme and
 * success payloads, the V1 and V2 payment-link pre-payment and success payloads,
 * the theme-webapp preview messages, and the QA URL seed — so the whole app has
 * exactly one answer to "what does this wire theme mean". Callers hand it
 * whatever their endpoint returned; there is no adapter to choose.
 *
 * The mode and colorConfig enums are normalized here rather than downstream, so
 * consumers only ever see the canonical UPPERCASE `ThemeConfig` enum and no
 * caller has to re-solve the wire casing. The field is `colorMode` in every
 * shape and on both sides of the boundary — the theme service protocol name,
 * the preview `themeChange` message and the QA `?colorMode=` seed all use it,
 * so there is no second spelling for a call site to respell and this function
 * reads one field. (PAA-3011, PAA-3012)
 *
 * @param wire Either wire shape, which may be absent.
 * @return The canonical theme config, or `undefined` when there is no theme.
 */
export const toThemeConfig = (wire?: WireThemeInput | null): ThemeConfig | undefined =>
  wire ? buildThemeConfig(wire) : undefined;

/*
 * The shared normalization every wire shape funnels through.
 *
 * Reads the nested and the flat spelling of the seeds and branding in one pass.
 * The field sets are disjoint — a payload carries `seedColors.colorBrand` or
 * `colorBrand`, never both — so the reads cannot collide and no shape detection
 * is needed.
 *
 * @param wire Either wire shape.
 * @return The canonical theme config.
 */
const buildThemeConfig = (wire: WireThemeInput): ThemeConfig => {
  const nested = wire as ServerInvoiceRes['theme'];
  const flat = wire as PaymentPageThemeDto;

  const colorBrand = pickString(nested.seedColors?.colorBrand ?? flat.colorBrand);
  const colorText = pickString(nested.seedColors?.colorText ?? flat.colorText);
  const colorBackground = pickString(nested.seedColors?.colorBackground ?? flat.colorBackground);
  // Migration (PAA-3007): a legacy theme with no explicit colorConfig that only
  // carries an accent is a `SIMPLE` theme; leave it unset for advanced-shaped
  // themes so the SDK default (advanced) honours the explicit colours.
  const colorConfig =
    normalizeEnum(wire.colorConfig, THEME_COLOR_CONFIGS) ??
    (colorBrand && !colorText && !colorBackground ? 'SIMPLE' : undefined);
  // A SIMPLE theme supplies the accent only — its background and ink are
  // synthesised from that accent, so any colour the API still sends for them is
  // a leftover from an earlier ADVANCED save and is dropped instead of painting
  // surfaces the merchant no longer controls. (PAA-3011)
  const isSimple = colorConfig === 'SIMPLE';
  const branding = nested.brandingSettings;

  return {
    isDefault: wire.isDefault,
    colorMode: normalizeEnum(wire.colorMode, THEME_MODES),
    colorConfig,
    seedColors: {
      colorBrand,
      colorText: isSimple ? undefined : colorText,
      colorBackground: isSimple ? undefined : colorBackground,
    },
    brandingSettings: {
      displayName: branding?.displayName ?? flat.displayName,
      primaryLogoUrl: branding?.primaryLogoUrl ?? flat.primaryLogoUrl,
      squareLogoUrl: branding?.squareLogoUrl ?? flat.squareLogoUrl,
      websiteUrl: branding?.websiteUrl ?? flat.websiteUrl,
      helpCenterUrl: branding?.helpCenterUrl ?? flat.helpCenterUrl,
      termsUrl: branding?.termsUrl ?? flat.termsUrl,
      privacyUrl: branding?.privacyUrl ?? flat.privacyUrl,
      customerSupportEmail: branding?.customerSupportEmail ?? flat.customerSupportEmail,
      customerSupportPhone: branding?.customerSupportPhone ?? flat.customerSupportPhone,
    },
  };
};

/**
 * The mode actually painted after `SYSTEM` is resolved (`LIGHT` / `DARK`).
 * Re-exported from the branding SDK so theme consumers share one spelling.
 */
export type ResolvedThemeMode = ResolvedMode;

/*
 * Map a canonical `ThemeColorMode` to the drop-in element's lowercase mode.
 *
 * `resolvedMode`, when supplied, is the already-resolved paint mode from
 * `useResolvedThemeMode` — it wins so a live `SYSTEM` flip is honored. Without
 * it the mode is resolved here at call time.
 *
 * @param mode The canonical theme mode.
 * @param resolvedMode The mode already resolved for paint, when known.
 * @return The drop-in element's own mode spelling.
 */
const resolveDropInMode = (mode?: ThemeColorMode, resolvedMode?: ResolvedThemeMode): 'light' | 'dark' =>
  (resolvedMode ?? resolveMode(mode ?? 'LIGHT')) === 'DARK' ? 'dark' : 'light';

/*
 * The payment drop-in (RHS) appearance.
 *
 * The drop-in is a *locked* surface: the paper and ink are the resolved mode's
 * defaults and only the accent follows the merchant brand. `colorText` and the
 * raw merchant `colorBackground` are never forwarded — the latter would let a
 * saturated merchant background tint the payment surface, which must stay
 * neutral (ADV-6447, PAA-3012).
 *
 * `generateDropinAppearance` is the branding SDK's own locked-surface
 * derivation, so we take its resolved `colorBrand` (it validates the seed and
 * falls back to the default accent) rather than forwarding the raw merchant
 * string, then re-ground paper and ink on the resolved mode's defaults. That
 * matches how hosted-checkout does it (PAA-3011), keeping the two products'
 * drop-in surfaces in step.
 *
 * `resolvedMode` comes from `useResolvedThemeMode` where a caller renders under
 * the shell, so a `SYSTEM` theme re-derives live when the device flips. Callers
 * that only hold the config (the V2 payment element) omit it and `SYSTEM` is
 * resolved here — the option object is rebuilt whenever the theme data changes,
 * so it is correct at mount and re-resolves on any later theme update.
 *
 * @param theme The canonical theme, if one is in scope.
 * @param resolvedMode The mode already resolved for paint, when known.
 * @return The drop-in appearance, or `undefined` when there is no brand accent.
 */
export const buildDropInAppearance = (
  theme?: ThemeConfig,
  resolvedMode?: ResolvedThemeMode
): DropInAppearance | undefined => {
  const seeds = effectiveSeedColors(theme);
  if (!seeds?.colorBrand) return undefined;
  const mode = resolvedMode ?? resolveMode(theme?.colorMode ?? 'LIGHT');
  // Feed the SDK the ALREADY-RESOLVED mode, not the raw `theme.colorMode`: when a
  // caller supplies `resolvedMode` (the shell's live SYSTEM paint), the drop-in's
  // paper/ink must re-derive for that mode too, otherwise a SYSTEM theme flipped
  // to dark would keep light-mode paper while the appearance mode said dark. The
  // SDK takes the mode under its own `mode` key. (PAA-3188, PAA-3012)
  const { variables } = generateDropinAppearance({
    colorConfig: theme?.colorConfig,
    mode,
    seedColors: seeds,
  });
  // The drop-in is a fixed "paper" surface, like the invoice card — the LOCKED
  // paper ground. Pass the SDK's variables through UNCHANGED: the accent carries
  // through, and the paper/ink are the resolved mode's locked pair (LIGHT: white
  // paper + near-black ink; DARK: hue-tinted `darkPaperBackground` + `#f5f6f7`
  // ink) — identical to the invoice card, never the raw merchant seeds. (PAA-3188)
  return {
    mode: resolveDropInMode(theme?.colorMode, mode),
    variables,
  };
};
